import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import { ShieldCheck, ShieldAlert, Copy, Check } from 'lucide-react';
import { apiGet, apiPost } from '../../api';
import { setTwoFAEnabled } from '../../slices/authSlice';
import ConfirmationModal from '../../components/ConfirmationModal';
import OtpInput from '../../components/OtpInput';

const TwoFactorAuth = () => {
  const dispatch = useDispatch();
  const enabled = useSelector((s) => s.auth.user?.twoFA?.enabled);
  const [setup, setSetup] = useState(null); // { secret, otpauth_url }
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const err = (e, fallback) => toast.error(e.response?.data?.message || fallback);

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await apiGet('/api/admin/account/2fa/setup');
      if (res.data.success) {
        setSetup(res.data.data);
        setCode('');
      } else toast.error(res.data.message || 'Failed to start setup');
    } catch (e) {
      err(e, 'Failed to start setup');
    } finally {
      setLoading(false);
    }
  };

  const enable = async (token = code) => {
    if (token.length !== 6 || !setup) return;
    setLoading(true);
    try {
      const res = await apiPost('/api/admin/account/2fa/verify', {
        tempSecret: setup.secret,
        token,
      });
      if (res.data.success) {
        toast.success(res.data.message || '2FA has been enabled.');
        dispatch(setTwoFAEnabled(true));
        setSetup(null);
        setCode('');
      } else toast.error(res.data.message || 'Invalid code');
    } catch (e) {
      err(e, 'Invalid code');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const res = await apiPost('/api/admin/account/2fa/disable', { token: code });
      if (res.data.success) {
        toast.success(res.data.message || '2FA has been disabled.');
        dispatch(setTwoFAEnabled(false));
        setShowDisable(false);
        setCode('');
      } else toast.error(res.data.message || 'Invalid code');
    } catch (e) {
      err(e, 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(setup.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const btnPrimary =
    'rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50';

  return (
    <div className="max-w-lg mx-auto">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Management
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground mb-1">
        Two-Factor <span className="text-gradient-ginox">Auth</span>
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Add an authenticator app as a second layer of security
      </p>

      <div className="border-t border-white/[0.08] pt-6">
        {/* Current status */}
        <div
          className={`flex items-center gap-3 rounded-lg border px-4 py-3 mb-6 ${
            enabled
              ? 'border-ginox-green/30 bg-ginox-green/[0.06]'
              : 'border-border/70 bg-white/[0.02]'
          }`}
        >
          {enabled ? (
            <ShieldCheck className="h-5 w-5 shrink-0 text-ginox-green" />
          ) : (
            <ShieldAlert className="h-5 w-5 shrink-0 text-muted-foreground" />
          )}
          <div>
            <p className="text-sm font-medium text-foreground">
              Authenticator app is {enabled ? 'enabled' : 'not enabled'}
            </p>
            <p className="text-xs text-muted-foreground">
              Email verification stays active on every login regardless of this setting.
            </p>
          </div>
        </div>

        {/* Disable flow */}
        {enabled && (
          <div className="space-y-4">
            <h2 className="text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60">
              Disable Authenticator
            </h2>
            <p className="text-xs text-muted-foreground">
              Enter a current code from your authenticator app to turn it off.
            </p>
            <OtpInput value={code} onChange={setCode} disabled={loading} />
            <button
              type="button"
              onClick={() => setShowDisable(true)}
              disabled={code.length !== 6 || loading}
              className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-2 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
            >
              Disable 2FA
            </button>
          </div>
        )}

        {/* Enable flow */}
        {!enabled && !setup && (
          <button type="button" onClick={startSetup} disabled={loading} className={btnPrimary}>
            {loading ? 'Preparing...' : 'Enable authenticator app'}
          </button>
        )}

        {!enabled && setup && (
          <div className="space-y-5">
            <div>
              <h2 className="text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60 mb-3">
                1. Scan the QR code
              </h2>
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-white p-3">
                  <QRCodeSVG value={setup.otpauth_url} size={132} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Or enter this key manually:
                  </p>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="flex w-full items-center gap-2 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-left transition-colors hover:bg-white/[0.05]"
                  >
                    <code className="flex-1 break-all font-mono text-xs text-foreground">
                      {setup.secret}
                    </code>
                    {copied ? (
                      <Check className="h-4 w-4 shrink-0 text-ginox-green" />
                    ) : (
                      <Copy className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60 mb-3">
                2. Enter the 6-digit code
              </h2>
              <OtpInput
                value={code}
                onChange={setCode}
                onComplete={enable}
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => enable()}
                disabled={code.length !== 6 || loading}
                className={btnPrimary}
              >
                {loading ? 'Verifying...' : 'Verify & Enable'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSetup(null);
                  setCode('');
                }}
                disabled={loading}
                className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDisable}
        onClose={() => setShowDisable(false)}
        onConfirm={disable}
        title="Disable 2FA"
        message="Are you sure you want to disable authenticator-app verification for your account?"
        confirmText="Disable 2FA"
        loading={loading}
        loadingText="Disabling..."
        danger
      />
    </div>
  );
};

export default TwoFactorAuth;
